import React, {useState, useEffect, useCallback, useMemo} from 'react';
import {EZPRTheme} from '../../_theme';
import PropTypes from 'prop-types';
import {Autocomplete, Button, ButtonGroup, CircularProgress, Popper, TextField, ThemeProvider} from '@mui/material';
import {ezprOrange, useDebounce} from '../../_helpers';
import {getItemCodeDescLambda} from '../../../utils/functionSignatures';
import {MasterDataController} from '../../../controllers/MasterDataController';
import {BrickContext} from 'olympe';

/**
 * @enum {string}
 */
ItemCodeDescriptionDropdown.tabs = {
    SUGGESTION: 'Suggestion',
    ALL: 'All',
};

/**
 * @param {ItemCodeDescriptionDropdown.propTypes} props
 * @return {JSX.Element}
 */
export function ItemCodeDescriptionDropdown(props) {
    const style = {
        width: '100%',
        height: '100%',
    };

    const id = 'id-' + (Date.now() * Math.random());

    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState([]);
    const [currentTab, setCurrentTab] = useState(props.enableSuggestions ?
        ItemCodeDescriptionDropdown.tabs.SUGGESTION :
        ItemCodeDescriptionDropdown.tabs.ALL);

    const [isPerformingQuery, setIsPerformingQuery] = useState(false);
    const [textInput, setTextInput] = useState('');
    const debouncedTextInput = useDebounce(textInput, 800);

    useEffect(async () => {
        if (!open) {
            return;
        }

        if (!props.plant.value) {
            setOptions([]);
            return;
        }

        if (currentTab === ItemCodeDescriptionDropdown.tabs.SUGGESTION && !props.l3Category) {
            setOptions([]);
            props.onChange({label: '', value: ''});
            return;
        }

        try {
            setIsPerformingQuery(true);
            let newOptions = [];

            if (currentTab === ItemCodeDescriptionDropdown.tabs.SUGGESTION) {
                const codeValue = props.itemCodeSearchProp === 'code' ? debouncedTextInput : '';
                const nameValue = props.itemCodeSearchProp === 'name' ? debouncedTextInput : '';
                const l3CategoryCode = props.l3Category.split(' - ')[0].trim();

                const response = await getItemCodeDescLambda(
                    props.context,
                    props.getItemCodeDesc,
                    props.plant.value,
                    l3CategoryCode,
                    codeValue,
                    nameValue,
                );

                const parsedResponse = JSON.parse(response);
                newOptions = parsedResponse.map((item) => {
                    const description = item.maktx ? ` - ${item.maktx}` : '';
                    const materialNumber = item.matnr.replace(/^0+/, '');
                    return {
                        label: `${materialNumber}${description}`,
                        value: materialNumber,
                    };
                });
            } else if (currentTab === ItemCodeDescriptionDropdown.tabs.ALL) {
                const searchInput = debouncedTextInput?.split(' - ')[0].trim();
                const response = await MasterDataController.fetchMasterData(
                    props.getMasterData,
                    props.context,
                    'Item Code',
                    searchInput,
                    undefined,
                    props.plant,
                    undefined,
                    undefined,
                    props.itemCodeSearchProp,
                    props.l3Category,
                    props.userId,
                );
                const parsedResponse = JSON.parse(response);
                newOptions = parsedResponse.map((item) => {
                    const description = item.maktx ? ` - ${item.maktx}` : '';
                    const materialNumber = item.matnr.replace(/^0+/, '');
                    return {
                        label: `${materialNumber}${description}`,
                        value: materialNumber,
                    };
                });
            }

            setOptions(newOptions);
        } catch (error) {
            console.error(error);
        } finally {
            setIsPerformingQuery(false);
        }
    }, [debouncedTextInput, props.l3Category, props.plant, props.itemCodeSearchProp, currentTab, open]);

    const displayedOptions = useMemo(() => options, [options, currentTab]);

    const handleChange = (_, newValue) => {
        if (!newValue) {
            props.onChange({label: '', value: ''});
        } else {
            props.onChange(newValue);
        }
    };

    const handleInputChange = (event, newInputValue, reason) => {
        if (reason === 'input') {
            setTextInput(newInputValue);
            if (newInputValue && newInputValue.length > 0) {
                setCurrentTab(ItemCodeDescriptionDropdown.tabs.ALL);
            }
        }
    };

    const handleChangeTab = (tabName) => {
        setCurrentTab(tabName);
    };

    const popperWithTabs = useCallback(
        (params) => (
            <Popper {...params}
                className="custom-dropdown-menu-with-buttons"
                placement="bottom-start"
                modifiers={[{name: 'flip', enabled: false}]}>
                <ButtonGroup color="primary" aria-label="outlined primary button group">
                    <Button
                        variant="text"
                        onClick={() => handleChangeTab(ItemCodeDescriptionDropdown.tabs.SUGGESTION)}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                            color:
                                currentTab === ItemCodeDescriptionDropdown.tabs.SUGGESTION ?
                                    ezprOrange :
                                    '#CCC',
                        }}
                    >
                        {ItemCodeDescriptionDropdown.tabs.SUGGESTION}
                    </Button>
                    <Button
                        variant="text"
                        onClick={() => handleChangeTab(ItemCodeDescriptionDropdown.tabs.ALL)}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                            color:
                                currentTab === ItemCodeDescriptionDropdown.tabs.ALL ?
                                    ezprOrange :
                                    '#CCC',
                        }}
                    >
                        {ItemCodeDescriptionDropdown.tabs.ALL}
                    </Button>
                </ButtonGroup>
                {params.children}
            </Popper>
        ),
        [currentTab],
    );

    return (
        <ThemeProvider theme={EZPRTheme}>
            <Autocomplete
                ListboxProps={{className: 'popup-scroller'}}
                data-cy={'item_code_desc_autocomplete'}
                id={id}
                disabled={props.disabled}
                PopperComponent={props.enableSuggestions ? popperWithTabs : undefined}
                options={displayedOptions}
                getOptionLabel={(option) => option.label}
                fullWidth
                loading={isPerformingQuery || props.loading}
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                style={style}
                size="small"
                value={props.selectedValue}
                onChange={handleChange}
                onInputChange={handleInputChange}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        variant={'outlined'}
                        rows={1}
                        placeholder={'Please select an item code from the dropdown'}
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                                <>
                                    {(isPerformingQuery || props.loading) && <CircularProgress color="inherit" size={20} />}
                                    {params.InputProps.startAdornment}
                                </>
                            ),
                        }}
                        error={props.error}
                    />
                )}
                renderOption={(liProps, option) => (
                    <li {...liProps} data-cy="item_code_desc_option">
                        {option.label}
                    </li>
                )}
            />
        </ThemeProvider>
    );
}

ItemCodeDescriptionDropdown.propTypes = {
    context: PropTypes.instanceOf(BrickContext).isRequired,
    error: PropTypes.bool,
    disabled: PropTypes.bool,
    getItemCodeDesc: PropTypes.object,
    l3Category: PropTypes.string,
    plant: PropTypes.object,
    itemCodeSearchProp: PropTypes.string,
    onChange: PropTypes.func,
    selectedValue: PropTypes.object,
    getMasterData: PropTypes.object,
    userId: PropTypes.string,
    enableSuggestions: PropTypes.bool,
    loading: PropTypes.bool,
};
